import { OpenQuestionTeacherPin, PinType } from '@lessonup/pins-shared';
import { every } from 'lodash';
import {
  compareOpenQuestionAnswers,
  OpenQuestionComparisonSettings,
} from '../../utils/open-question-answer-comparison';
import { AssignmentPin } from '../assignment';
import {
  AnswerByCategory,
  OpenQuestion,
  PinUtils,
  Poll,
  QuizQuestion,
  SortingQuestion,
  FillInTheBlanks,
  WordCloud,
  DragAndDrop,
} from '../common';
import { AssignmentEntry } from './AssignmentEntry';

export type SlidePinEntry = PinEntry & {
  type: 'SLIDE';
};

export interface OpenQuestionPinEntry extends PinEntry<OpenQuestion.Answer> {
  type: 'OPEN_QUESTION';
}

export type QuizPinEntry = PinEntry<QuizQuestion.Answer, QuizQuestion.AnswerResult> & {
  type: 'MULTIPLE_CHOICE';
};

export interface WordCloudPinEntry extends PinEntry<WordCloud.Answer> {
  type: 'WORD_CLOUD';
}

export interface PollPinEntry extends PinEntry<Poll.Answer> {
  type: 'POLL';
}

export interface SortingQuestionPinEntry extends PinEntry<SortingQuestion.Answer> {
  type: 'SORTING_QUESTION';
}

export interface AnswerByCategoryPinEntry extends PinEntry<AnswerByCategory.Answer> {
  type: 'ANSWER_BY_CATEGORY';
}

export interface FillInTheBlanksPinEntry extends PinEntry<FillInTheBlanks.Answer, FillInTheBlanks.AnswerResult> {
  type: 'FILL_IN_THE_BLANKS';
}

export interface DragAndDropPinEntry extends PinEntry<DragAndDrop.Answer, DragAndDrop.AnswerResult> {
  type: 'DRAG_AND_DROP';
}

export type AnyPinEntry =
  | QuizPinEntry
  | SlidePinEntry
  | OpenQuestionPinEntry
  | WordCloudPinEntry
  | PollPinEntry
  | SortingQuestionPinEntry
  | AnswerByCategoryPinEntry
  | FillInTheBlanksPinEntry
  | DragAndDropPinEntry;

export type InteractivePinEntry =
  | QuizPinEntry
  | WordCloudPinEntry
  | OpenQuestionPinEntry
  | AnswerByCategoryPinEntry
  | PollPinEntry
  | FillInTheBlanksPinEntry
  | SortingQuestionPinEntry
  | DragAndDropPinEntry;

export type AnyAnswer =
  | OpenQuestion.Answer
  | QuizQuestion.Answer
  | WordCloud.Answer
  | Poll.Answer
  | SortingQuestion.Answer
  | AnswerByCategory.Answer
  | FillInTheBlanks.Answer
  | DragAndDrop.Answer;

// at this time, only the quiz question has a result
export type AnyAnswerResult = QuizQuestion.AnswerResult | OpenQuestion.AnswerResult | FillInTheBlanks.AnswerResult;

export type AnyAnswerResultType = AnyAnswerResult['type'];

// TODO: it would be better to model this type in a way there its exclicit what the answers and result types are, based on the pin type
export interface PinEntry<
  A = AnyAnswer | undefined,
  R = AnyAnswerResult | undefined,
  T = AnyAnswerResultType | undefined,
> {
  pinId: string;
  type: PinType;
  done?: boolean;
  answer?: A; // latest answer
  /**
   * Calculated result based on the correctAnswer in the assignmentMeta
   */
  result?: R;
  resultType?: T;
}

/**
 * PinEntry is a namespace containing the user answers for a single pin
 * This is part of AssignmentEntry
 */
export namespace PinEntry {
  export type WithDisplayName = {
    displayName?: string;
  } & AnyPinEntry;

  export type UpdateData = Partial<Pick<AnyPinEntry, 'answer' | 'result' | 'done' | 'resultType'>>;

  export type Slide = SlidePinEntry;
  export type OpenQuestion = OpenQuestionPinEntry;
  export type QuizQuestion = QuizPinEntry;
  export type WordCloud = WordCloudPinEntry;
  export type AnswerByCategory = AnswerByCategoryPinEntry;
  export type Poll = PollPinEntry;
  export type FillInTheBlanks = FillInTheBlanksPinEntry;
  export type SortingQuestion = SortingQuestionPinEntry;
  export type DragAndDrop = DragAndDropPinEntry;
}

export namespace PinEntry {
  export function isDone(entry: AnyPinEntry): boolean {
    return !!entry.done;
  }

  export function isDragAndDrop(entry: AnyPinEntry): entry is PinEntry.DragAndDrop {
    return entry.type == 'DRAG_AND_DROP';
  }

  export function isOpenQuestion(entry: AnyPinEntry): entry is PinEntry.OpenQuestion {
    return entry.type == 'OPEN_QUESTION';
  }

  export function isFillInTheBlanks(entry: AnyPinEntry): entry is PinEntry.FillInTheBlanks {
    return entry.type == 'FILL_IN_THE_BLANKS';
  }

  export function areOpenQuestions(entries: AnyPinEntry[]): entries is PinEntry.OpenQuestion[] {
    return every(entries, (entry) => isOpenQuestion(entry));
  }

  export function isWordCloud(entry: AnyPinEntry): entry is PinEntry.WordCloud {
    return entry.type == 'WORD_CLOUD';
  }

  export function areWordClouds(entries: AnyPinEntry[]): entries is PinEntry.WordCloud[] {
    return every(entries, (entry) => isWordCloud(entry));
  }

  export function isQuizQuestion(entry: AnyPinEntry): entry is PinEntry.QuizQuestion {
    return entry.type == 'MULTIPLE_CHOICE';
  }

  export function areQuizQuestions(entries: AnyPinEntry[]): entries is PinEntry.QuizQuestion[] {
    return every(entries, (entry) => isQuizQuestion(entry));
  }

  export function isSortingQuestion(entry: AnyPinEntry): entry is PinEntry.SortingQuestion {
    return entry.type == 'SORTING_QUESTION';
  }

  export function isSlide(entry: AnyPinEntry): entry is PinEntry.Slide {
    return entry.type == 'SLIDE';
  }

  export function areSlides(entries: AnyPinEntry[]): entries is PinEntry.Slide[] {
    return every(entries, (entry) => isSlide(entry));
  }

  export function isInteractive(entry: AnyPinEntry | undefined | null): entry is InteractivePinEntry {
    return !!entry && PinUtils.typeIsInteractive(entry.type);
  }

  export function create(pin: AssignmentPin): AnyPinEntry {
    return {
      pinId: pin._id,
      type: pin.type,
    } as AnyPinEntry;
  }

  export interface ByType {
    slide: PinEntry.Slide[];
    openQuestion: PinEntry.OpenQuestion[];
    question: PinEntry.QuizQuestion[];
    wordCloud: PinEntry.WordCloud[];
  }

  export function groupByType(entries: AnyPinEntry[]): ByType {
    const entryGroupByType: ByType = {
      slide: [],
      openQuestion: [],
      question: [],
      wordCloud: [],
    };
    entries.forEach((entry) => {
      if (isSlide(entry)) entryGroupByType.slide.push(entry);
      else if (isOpenQuestion(entry)) entryGroupByType.openQuestion.push(entry);
      else if (isQuizQuestion(entry)) entryGroupByType.question.push(entry);
      else if (isWordCloud(entry)) entryGroupByType.wordCloud.push(entry);
    });
    return entryGroupByType;
  }

  /**
   * Find all assignment entries that have the same answer text for a specific pin
   *
   * @param activeEntries - Array of assignment entries to search through
   * @param pinId - The ID of the pin to check
   * @param toggledAnswerText - The answer text to find matches for
   * @param settings - Optional settings for comparison (capitalization and diacritics)
   * @returns Array of assignment entries with matching answers
   */
  export function findOpenQuestionEntriesWithTheSameAnswer(
    activeEntries: AssignmentEntry[],
    pinId: string,
    toggledAnswerText: string,
    settings: OpenQuestionComparisonSettings
  ): AssignmentEntry[] {
    return activeEntries.filter((entry) => {
      const entryPinEntry = entry.entries[pinId];
      if (!entryPinEntry || !isOpenQuestion(entryPinEntry)) return false;

      const entryAnswerText = OpenQuestion.Answer.latestText(entryPinEntry.answer);
      if (!entryAnswerText) return false;

      return compareOpenQuestionAnswers(toggledAnswerText, entryAnswerText, settings);
    });
  }
}
